import {
  Table,
  Button,
  TextInput,
  NumberInput,
  Group,
  Stack,
  ActionIcon,
  Select,
  Center,
  Text,
} from '@mantine/core';
import { IconX, IconPlus, IconGripVertical } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useProposal } from '../../context/ProposalContext';
import { FEE_ACTIONS } from '../../reducers/feeReducer';
import {
  LumpSum,
  LumpSumRow,
  LumpSumEstimateAmountFee,
  LumpSumEstimateRangeFee,
  LumpSumEstimateRow,
  LumpSumEstimate,
  LumpSumType,
} from '@/interfaces/fees';
import { v4 as uuidv4 } from 'uuid';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ExpandingTextArea from '@/components/ExpandingTextArea';

interface LumpSumTableProps {
  fee: LumpSum;
  index: number;
}

interface LumpSumEstimateRowProps {
  row: LumpSumEstimateRow;
  rowIndex: number;
  onUpdate: (rowIndex: number, updatedRow: LumpSumEstimateRow) => void;
  onRemove: (rowIndex: number) => void;
  isEstimate: true;
}

interface LumpSumSimpleRowProps {
  row: LumpSumRow;
  rowIndex: number;
  onUpdate: (rowIndex: number, updatedRow: LumpSumRow) => void;
  onRemove: (rowIndex: number) => void;
  isEstimate: false;
}

function SortableRow({
  row,
  rowIndex,
  onUpdate,
  onRemove,
  isEstimate,
}: LumpSumEstimateRowProps | LumpSumSimpleRowProps) {
  const { t } = useTranslation();
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id: rowIndex,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    height: '100%',
    display: 'table-row',
  };

  const handleTypeChange = (value: string | null) => {
    if (!isEstimate) return;
    if (!value) return;
    let newFee;
    const currentFee = row.fee;

    switch (value) {
      case 'LumpSumAmount':
        if (currentFee.type === 'LumpSumRange') {
          newFee = { type: 'LumpSumAmount', amount: currentFee.top } as LumpSumEstimateAmountFee;
        } else {
          newFee = {
            type: 'LumpSumAmount',
            amount: currentFee.amount,
          } as LumpSumEstimateAmountFee;
        }
        break;
      case 'LumpSumRange':
        if (currentFee.type === 'LumpSumAmount') {
          newFee = {
            type: 'LumpSumRange',
            bottom: 0,
            top: currentFee.amount,
          } as LumpSumEstimateRangeFee;
        } else {
          newFee = {
            type: 'LumpSumRange',
            bottom: currentFee.bottom,
            top: currentFee.top,
          } as LumpSumEstimateRangeFee;
        }
        break;
      default:
        return;
    }
    onUpdate(rowIndex, { ...row, fee: newFee });
  };

  const renderLumpSumAmount = (row: LumpSumRow | LumpSumEstimateRow, rowIndex: number) => {
    if (!isEstimate) {
      const simpleRow = row as LumpSumRow;
      return (
        <NumberInput
          value={simpleRow.fee.amount}
          onChange={(value) =>
            onUpdate(rowIndex, {
              ...simpleRow,
              fee: { amount: Number(value) },
            })
          }
          maw={'6rem'}
        />
      );
    }

    const estimateRow = row as LumpSumEstimateRow;
    const fee = estimateRow.fee;

    if (fee.type === 'LumpSumAmount') {
      return (
        <NumberInput
          value={fee.amount}
          onChange={(value) =>
            onUpdate(rowIndex, {
              ...estimateRow,
              fee: {
                type: 'LumpSumAmount',
                amount: Number(value),
              },
            })
          }
          maw={'6rem'}
        />
      );
    } else {
      return (
        <Group>
          <NumberInput
            label={t('bottom')}
            value={fee.bottom}
            onChange={(value) =>
              onUpdate(rowIndex, {
                ...estimateRow,
                fee: {
                  type: 'LumpSumRange',
                  bottom: Number(value),
                  top: fee.top,
                },
              })
            }
            maw={'6rem'}
          />
          <NumberInput
            label={t('top')}
            value={fee.top}
            onChange={(value) =>
              onUpdate(rowIndex, {
                ...estimateRow,
                fee: {
                  type: 'LumpSumRange',
                  bottom: fee.bottom,
                  top: Number(value),
                },
              })
            }
            maw={'6rem'}
          />
        </Group>
      );
    }
  };

  return (
    <Table.Tr ref={setNodeRef} style={style}>
      <Table.Td style={{ verticalAlign: 'bottom' }}>
        <div style={{ cursor: 'grab', padding: '0.5rem' }} {...listeners} {...attributes}>
          <IconGripVertical size={20} />
        </div>
      </Table.Td>
      <Table.Td style={{ verticalAlign: 'bottom' }}>
        {isEstimate ? (
          <ExpandingTextArea
            value={row.description}
            onChange={(e) =>
              onUpdate(rowIndex, { ...row, description: e.target.value } as LumpSumEstimateRow)
            }
            placeholder={t('description')}
            min_max_rows={1}
          />
        ) : (
          <ExpandingTextArea
            value={row.description}
            onChange={(e) =>
              onUpdate(rowIndex, { ...row, description: e.target.value } as LumpSumRow)
            }
            placeholder={t('description')}
            min_max_rows={1}
          />
        )}
      </Table.Td>
      {isEstimate && (
        <Table.Td maw={'4rem'} style={{ verticalAlign: 'bottom' }}>
          <Select
            value={(row as LumpSumEstimateRow).fee.type}
            onChange={handleTypeChange}
            data={[
              { value: 'LumpSumAmount', label: t('lumpSumAmount') },
              { value: 'LumpSumRange', label: t('lumpSumRange') },
            ]}
          />
        </Table.Td>
      )}
      <Table.Td maw={'8rem'} style={{ verticalAlign: 'bottom' }}>
        {renderLumpSumAmount(row, rowIndex)}
      </Table.Td>
      <Table.Td style={{ verticalAlign: 'bottom' }} maw={'2rem'}>
        <Center>
          <ActionIcon variant="subtle" color="red" onClick={() => onRemove(rowIndex)}>
            <IconX size={20} />
          </ActionIcon>
        </Center>
      </Table.Td>
    </Table.Tr>
  );
}

export default function LumpSumTable({ fee, index }: LumpSumTableProps) {
  const { t } = useTranslation();
  const { dispatchProposal } = useProposal();

  const handleAddRow = () => {
    const newRow: LumpSumRow | LumpSumEstimateRow = {
      id: uuidv4(),
      description: '',
      fee: {
        type: 'LumpSumAmount',
        amount: 0,
      } as LumpSumEstimateAmountFee,
    };
    dispatchProposal({
      type: FEE_ACTIONS.ADD_TABLE_ROW,
      index,
      payload: newRow,
    });
  };

  const handleRemoveRow = (rowIndex: number) => {
    dispatchProposal({
      type: FEE_ACTIONS.REMOVE_TABLE_ROW,
      index,
      tableIndex: rowIndex,
    });
  };

  const handleUpdateRow = (rowIndex: number, updatedRow: LumpSumRow | LumpSumEstimateRow) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_TABLE_ROW,
      index,
      tableIndex: rowIndex,
      payload: updatedRow,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = active.id as number;
    const newIndex = over.id as number;

    const reorderedRows =
      fee.lumpSumType === 'feeEstimate'
        ? arrayMove(fee.table as LumpSumEstimateRow[], oldIndex, newIndex)
        : arrayMove(fee.table as LumpSumRow[], oldIndex, newIndex);

    dispatchProposal({
      type: FEE_ACTIONS.REORDER_TABLE_ROWS,
      index,
      payload: reorderedRows,
    });
  };

  const handleLumpSumTypeChange = (value: LumpSumType | null) => {
    if (!value) return;
    let newFee: LumpSum | LumpSumEstimate;
    if (value === 'feeEstimate' && fee.lumpSumType !== 'feeEstimate') {
      newFee = {
        ...fee,
        table: fee.table.map((row) => ({
          ...row,
          fee: {
            type: 'LumpSumAmount',
            amount: row.fee.amount,
          },
        })),
        lumpSumType: value,
      } as LumpSumEstimate;
    } else if (fee.lumpSumType === 'feeEstimate' && value !== 'feeEstimate') {
      newFee = {
        ...fee,
        table: fee.table.map((row) => ({
          ...row,
          fee: {
            amount: row.fee.type == 'LumpSumRange' ? row.fee.top : row.fee.amount,
          },
        })),
        lumpSumType: value,
      } as LumpSum;
    } else {
      newFee = {
        ...fee,
        lumpSumType: value,
      } as LumpSum;
    }

    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: newFee,
    });
  };

  return (
    <Stack>
      <Group>
        <Select
          label={t('lumpSumType')}
          value={fee.lumpSumType}
          data={
            [
              { value: 'directCharge', label: t('directCharge') },
              { value: 'feeEstimate', label: t('feeEstimate') },
              { value: 'cappedFee', label: t('cappedFee') },
            ] as { value: LumpSumType; label: string }[]
          }
          onChange={(value) => handleLumpSumTypeChange(value as LumpSumType)}
        />
      </Group>
      <DndContext onDragEnd={handleDragEnd}>
        <SortableContext
          items={fee.table.map((_, index) => index)}
          strategy={verticalListSortingStrategy}
        >
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th></Table.Th>
                <Table.Th>{t('description')}</Table.Th>
                {fee.lumpSumType === 'feeEstimate' && <Table.Th>{t('type')}</Table.Th>}
                <Table.Th>{t('fee')}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {fee.lumpSumType === 'feeEstimate'
                ? fee.table.map((row: LumpSumEstimateRow, rowIndex: number) => (
                    <SortableRow
                      key={row.id}
                      row={row}
                      rowIndex={rowIndex}
                      onUpdate={handleUpdateRow}
                      onRemove={handleRemoveRow}
                      isEstimate={true}
                    />
                  ))
                : fee.table.map((row: LumpSumRow, rowIndex: number) => (
                    <SortableRow
                      key={row.id}
                      row={row}
                      rowIndex={rowIndex}
                      onUpdate={handleUpdateRow}
                      onRemove={handleRemoveRow}
                      isEstimate={false}
                    />
                  ))}
            </Table.Tbody>
          </Table>
        </SortableContext>
      </DndContext>
      <Group justify="flex-end">
        <Button variant="light" leftSection={<IconPlus size={14} />} onClick={handleAddRow}>
          {t('addRow')}
        </Button>
      </Group>
    </Stack>
  );
}
